import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, XCircle, CheckCircle2, Scale, Calculator, Clock } from "lucide-react";
import { api } from "@/lib/api";

const brl = (c) => (Number(c || 0) / 100)
  .toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function VerifyCalculation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (!token) { setLoading(false); return; }
      try {
        const r = await api.get(`/public/liq/calc/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Não foi possível consultar");
      } finally { setLoading(false); }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-verify-calc">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Hermes" className="w-full h-full object-contain p-0.5"/>
          </div>
          <div>
            <div className="font-heading text-base font-semibold text-slate-900">
              Hermes Calc · Selo de Auditoria
            </div>
            <div className="text-xs text-slate-500">
              Conferência pública da memória de cálculo
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {loading && <div className="text-sm text-slate-500">Consultando registro público…</div>}
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-rose-700 shrink-0"/>
            <div>
              <div className="font-semibold text-slate-900">Consulta falhou</div>
              <div className="text-sm text-slate-700">{error}</div>
            </div>
          </div>
        )}

        {data && data.found === false && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-amber-700 shrink-0"/>
            <div>
              <div className="font-semibold text-slate-900">Selo não encontrado</div>
              <div className="text-sm text-slate-700">
                O token informado não corresponde a nenhum cálculo publicado.
                Verifique se o link foi copiado corretamente ou peça um novo PDF ao advogado responsável.
              </div>
            </div>
          </div>
        )}

        {data && data.found && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-emerald-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5 text-emerald-700"/>
                <div className="text-sm font-semibold text-emerald-800">
                  Selo verificado — registro auditável no Hermes
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Field label="Processo" value={data.process_number || "—"}/>
                <Field label="Reclamante" value={data.claimant?.name || "—"}/>
                <Field label="Reclamada" value={data.defendant?.name || "—"}/>
                <Field label="Vara/Órgão" value={data.court || "—"}/>
                <Field label="Período de apuração" value={`${data.calc_start} → ${data.calc_end}`}/>
                <Field label="Data da liquidação" value={data.liquidation_date || "—"}/>
                <Field label="Correção" value={data.correction_index || "—"}/>
                <Field label="Juros" value={data.interest_rule || "—"}/>
                <Field label="Motor" value={data.engine_version} mono/>
                <Field label="Emitido em"
                        value={data.generated_at ? new Date(data.generated_at).toLocaleString("pt-BR") : "—"}/>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-slate-700"/>
                <h3 className="font-semibold text-slate-900">Totais consolidados</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Principal"  value={brl(data.total_cents)}/>
                <Kpi label="Total atualizado" value={brl(data.total_updated_cents)} strong/>
                {data.total_deducoes_cents > 0 &&
                  <Kpi label="Deduções (INSS+IR)" value={"− " + brl(data.total_deducoes_cents)} negative/>}
                {data.total_liquido_cents > 0 &&
                  <Kpi label="Líquido a receber" value={brl(data.total_liquido_cents)} positive/>}
                {data.total_fees_cents > 0 &&
                  <Kpi label="Honorários e custas" value={brl(data.total_fees_cents)}/>}
              </div>
            </div>

            {data.verbas_summary?.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Scale className="w-4 h-4 text-slate-700"/>
                  <h3 className="font-semibold text-slate-900">Verbas — resumo</h3>
                </div>
                <div className="text-xs text-slate-500 mb-2">
                  Ordenadas pelo motor. Valores em reais. Deduções e honorários sinalizados.
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">Verba</th>
                      <th className="py-2 pr-3">Natureza</th>
                      <th className="py-2 pr-3 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.verbas_summary.map((v, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="py-2 pr-3 text-slate-800">{v.label || v.code}</td>
                        <td className="py-2 pr-3">
                          {v.is_deduction ?
                            <span className="text-rose-700 text-xs">dedução</span> :
                            v.is_fees ? <span className="text-sky-700 text-xs">honorários/custas</span> :
                            <span className="text-slate-500 text-xs">principal</span>}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono">{brl(v.total_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-100 p-4 text-xs text-slate-600 flex items-start gap-2">
              <Clock className="w-4 h-4 shrink-0 mt-0.5"/>
              <div>
                Este selo é gerado pelo motor determinístico do Hermes Calc.
                Todos os valores são derivados por Decimal 28 dígitos + banker's rounding.
                Nenhum arredondamento intermediário. Para memória detalhada
                (fórmulas, mês-a-mês, correção e juros), solicite o PDF integral ao
                advogado responsável.
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"text-slate-900 " + (mono ? "font-mono text-xs" : "text-sm")}>{value}</div>
    </div>
  );
}

function Kpi({ label, value, strong, positive, negative }) {
  const cls = positive ? "text-emerald-800"
            : negative ? "text-rose-700"
            : strong ? "text-slate-900" : "text-slate-700";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"font-mono " + (strong ? "text-lg font-bold " : "text-sm ") + cls}>{value}</div>
    </div>
  );
}
